import React, { useCallback, useEffect, useState } from "react";
import TablePaginated from "../../../components/TablePaginated/TablePaginated";
import CardDataNotFound from "../../../components/CardDataNotFound/CardDataNotFound";
import useDialogAlert from "../../../hooks/DialogAlert";
import { CommonMenuList, RedirectPage } from "../models/OptionsMenu";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { SacTableRow, StyledMenuProps } from "../models/SacSectionsTableProps";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "../../../config/routes";
import {
  ROW_PER_PAGE,
  RegisterDescriptionType,
  SacRegisterStatus,
  SacRegisterStatusIcons,
  SacRegisterStatusTooltip,
} from "../models/SearchFilterOptions";
import {
  calcPagesQuantity,
  convertUTCManausFormated,
  formatCNPJ,
} from "../../../utils";
import { TableSacColumns } from "../models/TableHeaderColumns";
import { SacRegister } from "../../../modules/information/models/GetSacRegisterDTO";
import { useSac } from "../../../contexts/sac/sacContext";
import { SacRegisterQuery } from "../../../modules/information/models/SacRegisterQuery";
interface ClosedCallsTabProps {
  dataFilter?: SacRegisterQuery | null;
}

const ClosedCallsTab: React.FC<ClosedCallsTabProps> = ({ dataFilter }) => {
  const [data, setData] = useState<SacTableRow[]>([]);
  const [errorData, setErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { snackbar } = useDialogAlert();
  const { getAllRegisters, setDataItemInView, setItemInView } = useSac();

  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const StyledMenu: React.FC<StyledMenuProps> = (data: any) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuItemClick = async (type: RedirectPage) => {
      setAnchorEl(null);
      switch (type) {
        case "viewOrder": {
          const itemToSave = data.data.dataComplete;
          if (itemToSave) {
            sessionStorage.setItem(
              "sac_active_item",
              JSON.stringify(itemToSave)
            );
          }
          setItemInView(itemToSave.refId);
          setDataItemInView(itemToSave);
          navigate(routesConfig.INFORMATION().SAC_DETAILS);
          break;
        }
        case "deleteOrder":
          break;
        default:
          break;
      }
    };

    const handleClose = () => {
      setAnchorEl(null);
    };
    return (
      <div>
        <IconButton onClick={handleClickListItem}>
          <StyledIcon iconType="three-vertical-points" />
        </IconButton>
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "lock-button",
            role: "listbox",
          }}
          sx={{
            minWidth: 300,
          }}
        >
          {CommonMenuList.map((option, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                handleMenuItemClick(option.type);
              }}
              sx={{
                minWidth: 200,
                fontSize: 14,
                fontFamily: "Open Sans",
                "&.Mui-selected": {
                  color: "shadesOfDark.dark",
                  backgroundColor: "shadesOfDark.ultraLight",
                  fontWeight: 700,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "shadesOfDark.ultraLight",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  const getRegisterData = useCallback(async (query: SacRegisterQuery) => {
    try {
      setErrorData(false);
      setIsLoadingData(true);
      const response = await getAllRegisters?.({
        ...query,
        status: SacRegisterStatus.CLOSED,
      });
      const newArray = response?.content;
      setTotal(response?.total ?? 0);
      if (newArray?.length === 0) {
        setErrorData(true);
      }

      const newDate: SacTableRow[] =
        newArray?.map(
          (item: SacRegister): SacTableRow => ({
            id: item.id || "",
            refId: item.refId,
            type: RegisterDescriptionType[
              item.type as keyof typeof RegisterDescriptionType
            ],
            subsidiaryDescription: item.subsidiary.description || "",
            customerRequester: item.customerRequester || "",
            cnpj: formatCNPJ(item.customerCompany?.document) || "",
            cnpjDescription: item.customerCompany?.description || "",
            requestDate: convertUTCManausFormated(item.requestDate),
            dataComplete: item,
            status: (
              <StyledIcon
                iconType={
                  SacRegisterStatusIcons[
                    item.status as keyof typeof SacRegisterStatusIcons
                  ]
                }
                tooltip={{
                  title:
                    SacRegisterStatusTooltip[
                      item.status as keyof typeof SacRegisterStatusIcons
                    ],
                }}
              />
            ),
          })
        ) ?? [];
      setData(newDate);
    } catch (error) {
      snackbar({
        message: "Erro ao carregar tabela de rascunho. Tentar novamente",
        variant: "error",
      });
      setErrorData(true);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (dataFilter !== null) {
      getRegisterData(dataFilter as SacRegisterQuery);
      setCurrentPage(dataFilter?.page ?? 1);
    }
  }, [dataFilter]);

  const getbyPage = useCallback(
    (page: number) => {
      if (dataFilter !== null && page) {
        const pageFilter = { ...dataFilter, page: page };
        setCurrentPage(page);
        getRegisterData(pageFilter as SacRegisterQuery);
      }
    },
    [dataFilter, setCurrentPage]
  );

  return (
    <>
      {errorData ? (
        <CardDataNotFound
          icon="data-not-found"
          title="Não foi encontrado resultado"
          subtitle="Informe outros critérios de busca e consulte novamente"
        />
      ) : (
        <TablePaginated
          columns={TableSacColumns}
          data={data.map((item) => ({
            ...item,
            icon: <StyledMenu data={item} />,
          }))}
          isLoadingData={isLoadingData}
          loadFromServer
          onChangePage={(newPage) => {
            getbyPage(newPage);
          }}
          rowsPerPage={10}
          paginationProps={{
            pagesTotal: calcPagesQuantity(ROW_PER_PAGE, total),
            page: currentPage,
          }}
          resultsTotal={total}
        />
      )}
    </>
  );
};

export default ClosedCallsTab;
